/**
 * The project's "Tables" tab: lists the project's tab specs, opens one, and
 * creates, renames, duplicates and deletes them. Reads the project from the
 * project store. In a project without a questionnaire (`dataOnly`) a new
 * spec's tables count who answered: there is no routing to say who was asked.
 */
import React, { useState } from "react"
import { useNavigate } from "react-router-dom"
import { Button, Table, Dropdown } from "antd"
import type { MenuProps } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faEllipsis, faPen, faPlus } from "@fortawesome/free-solid-svg-icons"
import { useProjectStore } from "stores/projectStore"
import { useUserStore } from "stores/userStore"
import { ROUTES } from "constants/routes"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import EmptyState from "shared/ui/EmptyState/EmptyState.component"
import ConfirmationModal from "shared/ui/ConfimationModal/ConfimationModal.component"
import ExportModal from "shared/ui/ExportModal/ExportModal.component"
import { notify } from "shared/ui/Notifications/notify"
import {
  useTabSpecList,
  useTabSpecMutations,
  validateSpecName,
} from "../../api/useTabSpecFiles"
import { createEmptySpec } from "../../types/tabSpecDocument"

type Prompt =
  | { kind: "create" }
  | { kind: "rename"; from: string }
  | { kind: "duplicate"; from: string }
  | null
type Row = { name: string }

const PROMPT_COPY: Record<NonNullable<Prompt>["kind"], { title: string; action: string }> = {
  create: { title: "New tab spec", action: "Create" },
  rename: { title: "Rename tab spec", action: "Rename" },
  duplicate: { title: "Duplicate tab spec", action: "Duplicate" },
}

const defaultNameOf = (prompt: NonNullable<Prompt>): string =>
  prompt.kind === "create" ? "" : prompt.kind === "rename" ? prompt.from : `${prompt.from} copy`

const failureText = (e: unknown): string =>
  e instanceof Error ? e.message : "Something went wrong."

export const TablesTab: React.FC<{ dataOnly?: boolean }> = ({ dataOnly = false }) => {
  const project = useProjectStore((s) => s.project)
  const getUserFullName = useUserStore((s) => s.getUserFullName)
  const navigate = useNavigate()
  const list = useTabSpecList(project.id, project.Created_by)
  const { create, rename, duplicate, remove } = useTabSpecMutations(project.id, project.Created_by)
  const [prompt, setPrompt] = useState<Prompt>(null)
  const [pendingDelete, setPendingDelete] = useState<string | null>(null)

  const names = list.data ?? []
  const open = (name: string): void => navigate(ROUTES.TAB_SPEC(project.id, name))

  // The mutations check the name as well; checking the rule here first
  // saves a request. A failure keeps the dialog open.
  const onPromptConfirm = async (name: string): Promise<void> => {
    const trimmed = name.trim()
    if (!prompt) return
    // Renaming to the unchanged name is a no-op, not a clash with itself.
    if (prompt.kind === "rename" && trimmed === prompt.from) { setPrompt(null); return }
    const problem = validateSpecName(trimmed)
    if (problem) { notify.error("Tab spec", problem); return }
    try {
      if (prompt.kind === "create") {
        await create(
          trimmed,
          createEmptySpec(trimmed, getUserFullName(), dataOnly ? "answered" : "asked")
        )
        open(trimmed)
      } else if (prompt.kind === "rename") await rename(prompt.from, trimmed)
      else await duplicate(prompt.from, trimmed)
      setPrompt(null)
    } catch (e) { notify.error("Tab spec", failureText(e)) }
  }

  const onDeleteConfirm = async (): Promise<void> => {
    if (!pendingDelete) return
    try {
      await remove(pendingDelete)
      setPendingDelete(null)
    } catch (e) { notify.error("Tab spec", failureText(e)) }
  }

  const rowMenu = (name: string): MenuProps["items"] => [
    { key: "rename", label: "Rename", onClick: () => setPrompt({ kind: "rename", from: name }) },
    { key: "duplicate", label: "Duplicate", onClick: () => setPrompt({ kind: "duplicate", from: name }) },
    { type: "divider" },
    { key: "delete", label: "Delete", danger: true, onClick: () => setPendingDelete(name) },
  ]

  // As the dashboards' tab: the table shows its own loading state, and the
  // empty state waits for the list.
  let content: React.ReactNode
  if (list.isError) {
    content = (
      <EmptyState
        message="The tab specs could not be loaded"
        subtitle={list.error instanceof Error ? list.error.message : "Unknown error"}
      />
    )
  } else if (names.length === 0 && !list.isPending) {
    content = (
      <EmptyState
        message="No tab specs yet"
        subtitle="A tab spec lists the tables you want from this project. Create one to start."
      />
    )
  } else {
    content = (
      <Table<Row>
        size="small" rowKey="name" loading={list.isPending} pagination={false}
        dataSource={names.map((name) => ({ name }))}
        columns={[
          {
            title: "Name", dataIndex: "name",
            render: (name: string) => (
              <button type="button" className="text-left font-medium hover:underline" onClick={() => open(name)}>{name}</button>
            ),
          },
          {
            title: "", key: "actions", width: 140,
            render: (_: unknown, row: Row) => (
              <div className="flex gap-2 justify-end">
                {/* DataQButton takes no aria-label: the spec's name, for screen readers only, names each Open. */}
                <DataQButton type="secondary" onClick={() => open(row.name)}>
                  <FontAwesomeIcon icon={faPen} />Open<span className="sr-only">{` ${row.name}`}</span>
                </DataQButton>
                <Dropdown menu={{ items: rowMenu(row.name) }} trigger={["click"]}>
                  <Button aria-label={`More actions for ${row.name}`} icon={<FontAwesomeIcon icon={faEllipsis} />} />
                </Dropdown>
              </div>
            ),
          },
        ]}
      />
    )
  }

  return (
    <div className="p-4 flex flex-col gap-3 h-full">
      <div className="flex items-center gap-2">
        <h2 className="text-base font-medium m-0">Tab specs</h2>
        <div className="ml-auto">
          <DataQButton type="primary" onClick={() => setPrompt({ kind: "create" })}>
            <FontAwesomeIcon icon={faPlus} />New tab spec
          </DataQButton>
        </div>
      </div>
      {content}
      {/* Mounted only while open, so each prompt starts from its own default name. */}
      {prompt && (
        <ExportModal
          title={PROMPT_COPY[prompt.kind].title}
          isVisible
          cancel={() => setPrompt(null)}
          confirm={(filename) => onPromptConfirm(filename)}
          ExportButtonText={PROMPT_COPY[prompt.kind].action}
          message="Tab spec name"
          defaultFilename={defaultNameOf(prompt)}
        />
      )}
      {pendingDelete !== null && (
        <ConfirmationModal
          title="Delete tab spec"
          isVisible
          message={`Delete the tab spec "${pendingDelete}"? This cannot be undone.`}
          confirmButtonText="Delete" confirmButtonType="danger"
          cancel={() => setPendingDelete(null)}
          confirm={() => void onDeleteConfirm()}
        />
      )}
    </div>
  )
}
